import { Dispatch, SetStateAction, FC, PropsWithChildren, type JSX, useId } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import {
  accordionRotatingChevronStyle,
  contentStyle,
  headingContainerStyle,
  itemStyle,
  triggerStyle,
} from './Accordion.css';
import { ChevronDownIcon } from '../../icons';
import { getClassNames } from '../../util/css';
import { Box } from '../Box/Box';
import { cardRecipe } from '../Card/Card.css';
import { indicatorButtonRecipe } from '../Expandable/Expandable.css';
import { Heading } from '../Heading/Heading';

type StateProps =
  | {
      value?: never;
      setValue?: never;
    }
  | {
      /** Controlled state of the expandable */
      value: string[];
      /** State setter for the controlled state. Required in order to use built-in Expandable controls. */
      setValue: Dispatch<SetStateAction<string[]>>;
    };

type Props = {
  /**
   * Id(s) of the item(s) that will be opened on load.
   */
  defaultValue?: string[];
} & StateProps;
interface CompoundAccordionProps extends FC<PropsWithChildren<Props>> {
  Item: typeof Item;
}

export const Accordion = (({ children, defaultValue, value, setValue }) => {
  return (
    <AccordionPrimitive.Root
      className={getClassNames(cardRecipe())}
      type="multiple"
      defaultValue={defaultValue}
      value={value}
      onValueChange={setValue}>
      {children}
    </AccordionPrimitive.Root>
  );
}) as CompoundAccordionProps;

interface ItemProps {
  /**
   * Determines whether the indicator button has a background or not.
   */
  hasChevronBg?: boolean;
  /**
   * Heading that will become the trigger of the Accordion.Item.
   */
  heading: string;
  /**
   * The correct heading level of the trigger inside the document.
   */
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * A unique id tied to the item. Used in conjunction with defaultValue.
   */
  id?: string;
  /**
   * Icon shown to the left of the text.
   */
  leftIcon?: JSX.Element;
  /**
   * Option to turn off the content padding of the item, useful for when the item contains an image only.
   */
  noContentPadding?: boolean;
}

const Item: FC<PropsWithChildren<ItemProps>> = ({
  children,
  hasChevronBg = true,
  heading,
  headingLevel = 'h3',
  id,
  leftIcon,
  noContentPadding,
}) => {
  const generatedId = useId();
  const value = id || generatedId;

  const indicatorButtonClassNames = getClassNames(
    indicatorButtonRecipe({ isTransparent: !hasChevronBg }),
    accordionRotatingChevronStyle,
  );

  return (
    <AccordionPrimitive.Item className={itemStyle} value={value}>
      <Heading as={headingLevel} size="2XS">
        <AccordionPrimitive.Trigger className={triggerStyle}>
          {leftIcon}
          <div className={headingContainerStyle({ hasLeftIcon: !!leftIcon })}>{heading}</div>
          <div className={indicatorButtonClassNames} aria-hidden="true">
            <ChevronDownIcon />
          </div>
        </AccordionPrimitive.Trigger>
      </Heading>
      <AccordionPrimitive.Content className={contentStyle}>
        <Box
          paddingTop={noContentPadding ? 0 : '2'}
          paddingX={noContentPadding ? 0 : '6'}
          paddingRight={{ md: noContentPadding ? 0 : '12' }}
          paddingBottom={noContentPadding ? 0 : '6'}>
          {children}
        </Box>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
};

Accordion.Item = Item;

Accordion.displayName = 'Accordion';
Item.displayName = 'Accordion.Item';
Accordion.Item.displayName = 'Accordion.Item';
